/**
 * useDealEventVisuals — visual + label helpers for the deal_events timeline (F2).
 *
 * Single source of truth for the read-only event timeline rendered in CrmTimeline.
 * Mirrors useLeadVisuals: pure constants + pure functions, no reactive state, no
 * composable lifecycle. Safe to import anywhere.
 *
 * Brand-only / monochrome doctrine (CRM light-mode): events carry NO hue. "ganho"
 * gets ink emphasis (dot n-slate-12); everything else — including "perda" — is muted
 * slate (data is monochrome; lost = cinza, never red). The icon set is utilitarian
 * lucide; color comes from the dot, not the glyph.
 *
 * Unknown / not-yet-emitted tipos (mudanca_sla, follow_up_realizado,
 * follow_up_ignorado, …) resolve to a null label so callers skip them — the
 * timeline degrades gracefully instead of crashing on a future backend event.
 */

import { objecaoLabel, objecaoDetalheLabel } from '../constants/objections.js';

// Maps deal_event tipo → lucide icon class.
export const EVENT_ICON = {
  mudanca_etapa: 'i-lucide-arrow-right-circle',
  tarefa_criada: 'i-lucide-plus-circle',
  tarefa_concluida: 'i-lucide-check-circle',
  ganho: 'i-lucide-trophy',
  perda: 'i-lucide-x-circle',
  reativacao: 'i-lucide-rotate-ccw',
  // Passo atrás: seta para trás. O card voltou um passo, o negócio segue vivo.
  objecao: 'i-lucide-undo-2',
  // Trilha definida pela assistente: a bifurcação. Não é "movido", é "classificado".
  mudanca_funil: 'i-lucide-git-fork',
  // Leitura de conversa: o olho. É o evento que responde "de onde veio esse dado?" —
  // sem ele o vendedor abriria um card com cinco campos preenchidos por ninguém.
  leitura: 'i-lucide-scan-eye',
};

// pt-BR para o motivo da leitura. Sem isso a linha diria "lido (esfriou)", que é
// vocabulário de código aparecendo na cara do vendedor.
const MOTIVO_LEITURA = {
  esfriou: 'a conversa esfriou',
  mudou_etapa: 'o card mudou de etapa',
  backfill: 'revisão do histórico',
  manual: 'a pedido',
};

// pt-BR labels for action-type slugs — mirrors backend Task::ACTION_TYPE_LABELS.
export const ACTION_TYPE_LABELS = {
  ligacao: 'Fazer ligação',
  proposta: 'Enviar proposta',
  follow_up: 'Fazer follow-up',
  solicitar_retorno: 'Solicitar retorno',
  catalogo: 'Enviar catálogo',
  reuniao: 'Agendar reunião',
  orcamento: 'Enviar orçamento',
  personalizada: 'Personalizada',
};

// Maps action-type slug → lucide icon class (F7: linhas de ação da Home).
// Utilitário e monocromático — a cor vem do contexto (ruby só em atraso), nunca
// do glifo. Espelha as chaves de ACTION_TYPE_LABELS.
export const ACTION_TYPE_ICON = {
  ligacao: 'i-lucide-phone',
  proposta: 'i-lucide-file-text',
  follow_up: 'i-lucide-repeat',
  solicitar_retorno: 'i-lucide-bell',
  catalogo: 'i-lucide-book-open',
  reuniao: 'i-lucide-calendar',
  orcamento: 'i-lucide-receipt',
  personalizada: 'i-lucide-circle-dot',
};

/**
 * Returns the lucide icon class for an action-type slug.
 * Unknown/future tipos fall back to the neutral dot-glyph.
 *
 * @param {string} tipo
 * @returns {string}
 */
export function actionTypeIconFor(tipo) {
  return ACTION_TYPE_ICON[tipo] ?? 'i-lucide-circle-dot';
}

/**
 * Returns the lucide icon class for a deal_event tipo.
 * Falls back to a neutral dot-glyph for unknown/future tipos.
 *
 * @param {string} tipo
 * @returns {string}
 */
export function eventIconFor(tipo) {
  return EVENT_ICON[tipo] ?? 'i-lucide-circle-dot';
}

/**
 * Builds the human pt-BR line for a deal_event, from its tipo + metadados.
 * Returns null for tipos we don't render (unknown/not-yet-emitted) so the caller
 * can filter them out without special-casing.
 *
 * @param {{ tipo: string, metadados?: object }} event
 * @returns {string|null}
 */
export function dealEventLabel(event) {
  const tipo = event?.tipo;
  const m = event?.metadados ?? {};
  switch (tipo) {
    case 'mudanca_etapa':
      return `Movido para ${m.para_nome ?? '—'}`;
    case 'tarefa_criada':
      return `Ação criada: ${ACTION_TYPE_LABELS[m.task_tipo] ?? 'Ação'}`;
    case 'tarefa_concluida':
      return `Ação concluída: ${ACTION_TYPE_LABELS[m.task_tipo] ?? 'Ação'}`;
    case 'ganho':
      return 'Venda ganha';
    case 'perda':
      return `Lead perdido${m.motivo_perda ? ' — ' + m.motivo_perda : ''}`;
    case 'objecao': {
      // "Travou em", não "perdido": a diferença entre objeção e motivo de perda tem de
      // aparecer na leitura, não só no banco.
      const nivel1 = objecaoLabel(m.objecao) || m.objecao || '—';
      const nivel2 = objecaoDetalheLabel(m.objecao_detalhe);
      // O detalhe é o que informa de verdade — "preço" e "a verba acabou" pedem ações
      // opostas. Quando ele existe, é ele que aparece.
      return nivel2 ? `Travou em ${nivel1}: ${nivel2}` : `Travou em ${nivel1}`;
    }
    case 'reativacao':
      return 'Cliente reativado';
    case 'mudanca_funil':
      // A trilha é o que decide quem atende e com que processo. Mostrar o destino basta —
      // de onde veio é sempre a trilha padrão de entrada.
      return `Trilha definida: ${m.para_funil_nome ?? '—'}`;
    case 'leitura': {
      // Diz QUANTOS campos e POR QUE agora. "Lido da conversa" sozinho não deixa
      // ninguém julgar se vale conferir; "5 campos" deixa.
      const n = Number(m.campos_lidos) || 0;
      const campos = n === 1 ? '1 campo lido' : `${n} campos lidos`;
      const motivo = MOTIVO_LEITURA[m.motivo];
      return motivo
        ? `${campos} da conversa — ${motivo}`
        : `${campos} da conversa`;
    }
    default:
      return null;
  }
}

/**
 * Whether an event carries positive ink emphasis (ganho) versus the muted slate
 * baseline shared by every other event — including perda (data is monochrome).
 *
 * @param {{ tipo: string }} event
 * @returns {boolean}
 */
export function isEmphasisEvent(event) {
  return event?.tipo === 'ganho';
}
